import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Panel, Pill, useLoad, Loading, act } from "@/components/common";

const MSG = { connected: ["success", "Basecamp connected"], failed: ["error", "Basecamp connection failed or was cancelled"], noaccount: ["error", "That Basecamp login has no Basecamp 3/4 account"] };

export function BasecampAdmin() {
  const { data: s, error, reload } = useLoad(() => api.get("/integrations/basecamp/status").then((r) => r.data));
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const m = MSG[params.get("basecamp")];
    if (m) { toast[m[0]](m[1]); params.delete("basecamp"); setParams(params, { replace: true }); }
  }, [params, setParams]);
  if (!s) return <Loading error={error} />;
  const connect = () => api.get("/integrations/basecamp/start").then((r) => window.location.assign(r.data.authorize_url)).catch((e) => toast.error(e.response?.data?.detail || "Could not start"));
  const poll = () => act(api.post("/integrations/basecamp/poll")).then((r) => { if (r) { toast.success(r.data.skipped ? "Not connected" : r.data.error ? "Check failed — see status" : `Checked ${r.data.scanned} uploads, ${r.data.new} new`); reload(); } });
  return (
    <Panel title="Basecamp" testid="basecamp-admin">
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">Status:
          <span data-testid="basecamp-status">{!s.configured ? <Pill tone="amber">keys not set up</Pill> : s.connected ? <Pill tone="green">connected{s.account_name ? ` to ${s.account_name}` : ""}</Pill> : <Pill tone="slate">not connected</Pill>}</span>
          {s.connected && <span className="text-xs text-slate-500">by {s.connected_by} · last check {s.last_poll ? s.last_poll.slice(0, 16).replace("T", " ") : "never"}</span>}
        </div>
        {s.last_error && <div className="rounded-md bg-red-50 px-3 py-2 text-red-700" data-testid="basecamp-error">{s.last_error}</div>}
        {!s.configured && (
          <div className="rounded-md bg-amber-50 p-3 text-amber-900" data-testid="basecamp-setup-help">
            Register an app at launchpad.37signals.com/integrations with redirect URI <code className="break-all font-mono text-xs">{s.redirect_uri}</code>, then give the Client ID and Client Secret to your Aspire developer to add to the server settings.
          </div>
        )}
        <p className="text-xs text-slate-500">Once connected, Aspire checks linked Basecamp projects for new uploads every 15 minutes. PMs link each project on its Basecamp deliveries tab and confirm suggested matches.</p>
        <div className="flex gap-2">
          {s.can_connect && !s.connected && <Button data-testid="basecamp-connect-btn" disabled={!s.configured} onClick={connect}>Connect Basecamp</Button>}
          {s.can_connect && s.connected && <Button variant="outline" data-testid="basecamp-disconnect-btn" onClick={() => window.confirm("Disconnect Basecamp? Automatic checks will stop.") && act(api.post("/integrations/basecamp/disconnect"), "Disconnected").then(reload)}>Disconnect</Button>}
          {s.connected && <Button variant="outline" data-testid="basecamp-poll-btn" onClick={poll}>Check now</Button>}
        </div>
      </div>
    </Panel>
  );
}
